import {ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  InsightEntityType,
  RecommendationGroupModel,
  RecommendationHolderType, RecommendationModel,
  RecommendationProcessModel,
  RecommendationStatus
} from "../../../models/recommendation.model";
import {CurrentSiteContextService} from "../../../../commons/angular/services/current-site-context.service";
import {filter, Observable, Subscription} from "rxjs";
import {PANEL_DATA, PanelService} from "../../../../commons/angular/services/panel.service";
import {
  RecommendationGroupsComponentStore
} from "../../state/recommendation-groups-component-store.service";
import {
  AiAgentSelectComponent
} from "../../../../ai-agents/angular/components/ai-agent-select/ai-agent-select.component";
import {AiAgentModel} from "../../../../ai-agents/models/ai-agent.model";
import {RecommendationAlertsService} from "../../services/recommendation-alerts.service";
import {RecommendationHttpService} from "../../services/recommendation-http.service";
import {ProcessTrackingStatusEnum} from "../../../../commons/types/process-tracking-status.enum";
import {getRecommendationProcessAgentId} from "../../state/recommendation-process.functions";
import {RecommendationProcessChannelService} from "../../services/recommendation-process-channel.service";

interface RecommendationGroupsPanelLayoutInput {
  holderType: RecommendationHolderType;
  holderId: string;
  entityType: InsightEntityType;
  allowGenerateRecommendations: boolean;
}

require('./recommendation-groups-panel-layout.scss');
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-recommendation-groups-panel-layout',
  templateUrl: './recommendation-groups-panel-layout.html',
  providers: [RecommendationGroupsComponentStore]
})
export class RecommendationGroupsPanelLayoutComponent implements OnInit, OnDestroy {

  @Output() onAccountSearch = new EventEmitter<string>();
  @Output() onClose = new EventEmitter<any>();
  @Output() openRoutePanelsByIds = new EventEmitter<string[]>();
  recommendationGroups$: Observable<RecommendationGroupModel[]>;
  selectedRecommendationGroup$: Observable<RecommendationGroupModel>;
  recommendations$: Observable<RecommendationModel[]>;

  loading$: Observable<boolean>;
  cursor$: Observable<string>;

  public generateRecommendationsAllowed: boolean = false;

  private _holderType: RecommendationHolderType;
  private _holderId: string;
  private _entityType: InsightEntityType;
  private _siteId: string;
  private _queryParams: {
    siteId: string;
    recommendationHolderType: RecommendationHolderType;
    recommendationHolderId: string;
    entityType: InsightEntityType;
  };
  private _agentSelectPanel: { afterClose$: Observable<any>; instance?: any } | null = null;
  private _processesMap: Map<string, string> = new Map();
  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(RecommendationGroupsComponentStore) private store: RecommendationGroupsComponentStore,
    @Inject(CurrentSiteContextService) private currentSiteContextService: CurrentSiteContextService,
    @Inject(PANEL_DATA) public data: RecommendationGroupsPanelLayoutInput,
    @Inject(PanelService) private panelService: PanelService,
    @Inject(RecommendationAlertsService) private recommendationAlertsService: RecommendationAlertsService,
    @Inject(RecommendationHttpService) private recommendationHttpService: RecommendationHttpService,
    @Inject(RecommendationProcessChannelService) private recommendationProcessChannelService: RecommendationProcessChannelService
  ) {
    this.recommendationGroups$ = this.store.recommendationGroups$;
    this.selectedRecommendationGroup$ = this.store.selectedRecommendationGroup$;
    this.recommendations$ = this.store.recommendations$;
    this.loading$ = this.store.loading$;
    this.cursor$ = this.store.cursor$;
  }

  ngOnInit(): void {
    this._holderId = this.data.holderId;
    this._holderType = this.data.holderType;
    this._entityType = this.data.entityType;
    this.generateRecommendationsAllowed = this.data.allowGenerateRecommendations;
    this._siteId = this.currentSiteContextService.currentSiteId;
    this._queryParams = {
      siteId: this._siteId,
      recommendationHolderId: this._holderId,
      recommendationHolderType: this._holderType,
      entityType: this._entityType,
    };
    this.loadRecommendations();

    this._subscriptions.push(
      this.recommendationProcessChannelService.getChannelsValues()
        .pipe(filter((process: RecommendationProcessModel) => this._processesMap.get(getRecommendationProcessAgentId(process)) === process.id))
        .subscribe((process: RecommendationProcessModel) => this._onProcessChanged(process))
    );
  }

  ngOnDestroy(): void {
    // Clean up: reset store and unsubscribe from Firebase
    this._processesMap.forEach(processId => this.recommendationProcessChannelService.unSubscribeById(processId));
    this._processesMap.clear();
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this.store.onResetRecommendationGroups();
  }

  loadRecommendations(): void {
    this.store.getRecommendationGroups(this._queryParams);
  }

  onFetchNext(): void {
    this.store.getRecommendationGroups(this._queryParams);
  }

  onSelectGroup(recommendationGroup: RecommendationGroupModel): void {
    const params: any = {
      siteId: this._siteId,
      holderId: this._holderId,
      holderType: this._holderType,
      group: recommendationGroup
    };

    this.store.selectGroupRecommendation(params);
  }

  onBack(): void {
    this.store.resetGroupRecommendation();
  }

  closePanel(): void {
    this.onClose.emit();
  }

  onSelectAgent(event: Event): void {
    event?.stopPropagation();

    this._agentSelectPanel = this.panelService.open(AiAgentSelectComponent, event, {
      processingAgents: Array.from(this._processesMap.keys())
    }, {
      position: {
        originX: 'center',
        originY: 'bottom'
      }
    });

    if (this._agentSelectPanel) {
      this._subscriptions.push(
        this._agentSelectPanel.instance.onAgentsLoaded.subscribe((agents: AiAgentModel[]) => {
          // The visit plan is both the holder and the entity of its recommendation processes
          this._subscriptions.push(
            this.recommendationHttpService.getRecommendationProcesses(this._holderId, this._holderId, agents.map(agent => agent.id))
              .subscribe((processes: RecommendationProcessModel[]) => {
                processes.forEach(process => this._trackProcess(process));
              })
          );
        }),
        this._agentSelectPanel.instance.onAgentSelected.subscribe((agent: AiAgentModel) => {
          this._subscriptions.push(
            this.recommendationHttpService.generateVisitPlanRecommendations(this._holderId, agent.id)
              .subscribe({
                next: (process: RecommendationProcessModel) => this._trackProcess(process),
                error: (error) => this.recommendationAlertsService.generateRecommendationsError(error?.error)
              })
          );
        }),
        // afterClose$ completes without a value when the panel is closed programmatically
        this._agentSelectPanel.afterClose$.subscribe({complete: () => this._agentSelectPanel = null})
      );
    }
  }

  goToRecommendation(groupAccountName: string, r: RecommendationModel): void {
    const accountName = groupAccountName || r.insightOutput?.insightEntities.find((i) => {
      return i.tag === "AccountName";
    })?.displayName

    if (!!accountName) {
      this.onAccountSearch.emit(accountName);
      // this.store.updateRecommendation(accountName);
    }
  }

  updateRecommendationFeedback($event: RecommendationModel): void {
    this.store.updateRecommendation($event);
  }

  markRecommendationAsRead($event: RecommendationModel): void {
    this.store.markRecommendationAsRead($event);
  }

  private _trackProcess(process: RecommendationProcessModel): void {
    const agentId: string = getRecommendationProcessAgentId(process);

    if (this._processesMap.has(agentId)) {
      return;
    }

    this.recommendationProcessChannelService.subscribeById(process.id);
    this._processesMap.set(agentId, process.id);
    this._updateProcessingAgents();
  }

  private _onProcessChanged(process: RecommendationProcessModel): void {
    const agentId: string = getRecommendationProcessAgentId(process);

    if (process.processStatus === ProcessTrackingStatusEnum.COMPLETED) {
      this._untrackProcess(agentId);
      this.store.onResetRecommendationGroups();
      this.loadRecommendations();
    } else if (process.processStatus === ProcessTrackingStatusEnum.FAIL) {
      this._untrackProcess(agentId);
      this.recommendationAlertsService.generateRecommendationsError();
    }
  }

  private _untrackProcess(agentId: string): void {
    this.recommendationProcessChannelService.unSubscribeById(this._processesMap.get(agentId));
    this._processesMap.delete(agentId);
    this._updateProcessingAgents();
  }

  private _updateProcessingAgents(): void {
    this._agentSelectPanel?.instance?.updateProcessingAgents(Array.from(this._processesMap.keys()));
  }

}
